"use client"

import { Card, CardContent } from "@/shared/components/ui/card"
import { Quote, Star } from "lucide-react"
import { useState, useEffect } from "react"
import { Button } from "@/shared/components/ui/button"
import { Link } from "react-router-dom"
import { api } from "@/shared/lib/api/ApiClient"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"

interface Testimonial {
  id: number
  rating: number
  comment: string
  created_at: string
  location?: string
  image?: string
  trip?: string
  package_title?: string
}

// Component for consistent testimonial card display
function TestimonialDisplayCard({ testimonial }: { testimonial: Testimonial }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const maxLength = 150
  const shouldTruncate = testimonial.comment.length > maxLength

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center mb-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <Star
            key={index}
            className={`h-4 w-4 ${
              index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    )
  }

  return (
    <>
      <Card className="bg-card border-border hover:shadow-lg transition-shadow duration-300 h-[400px] flex flex-col">
        <CardContent className="p-6 flex flex-col h-full">
          <div className="flex items-center mb-4">
            <Quote className="h-6 w-6 text-orange-500 mb-2" />
          </div>

          {renderStars(testimonial.rating)}

          <div className="flex-grow mb-4 min-h-[120px]">
            <p className="text-card-foreground text-pretty leading-relaxed text-sm line-clamp-4">
              "{shouldTruncate 
                ? testimonial.comment.substring(0, maxLength) + '...' 
                : testimonial.comment}"
            </p>
            {shouldTruncate && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="text-orange-500 hover:text-orange-600 text-sm font-medium mt-2 transition-colors"
              >
                Read More
              </button>
            )}
          </div>

          <div className="border-t border-border pt-3 mt-auto">
            <div className="flex items-center justify-between">
              <div>
                {testimonial.location && (
                  <p className="text-sm text-muted-foreground font-medium">{testimonial.location}</p>
                )}
                {testimonial.trip && (
                  <p className="text-xs text-orange-600 mt-1">{testimonial.trip}</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testimonial Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Quote className="h-5 w-5 text-orange-500" />
              Customer Review
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {renderStars(testimonial.rating)}
              <span className="text-sm text-muted-foreground">({testimonial.rating}/5 stars)</span>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-900 leading-relaxed italic text-base">
                "{testimonial.comment}"
              </p>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  {testimonial.location && (
                    <p className="font-medium text-gray-900">{testimonial.location}</p>
                  )}
                  {testimonial.trip && (
                    <p className="text-sm text-orange-600 mt-1">{testimonial.trip}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">
                    {new Date(testimonial.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function Testimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoading(true)
      const res = await api.testimonials.getHome()
      if (res.success) {
        setTestimonials((res.data || []) as Testimonial[])
        setError(null)
      } else {
        setError(res.error || 'Failed to load testimonials')
      }
      setLoading(false)
    }

    fetchTestimonials()
  }, [])

  useEffect(() => {
    if (testimonials.length > 0) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % Math.ceil(testimonials.length / 3))
      }, 10000)

      return () => clearInterval(timer)
    }
  }, [testimonials.length])

  const getVisibleTestimonials = () => {
    const start = currentIndex * 3
    return testimonials.slice(start, start + 3)
  }

  return (
    <section className="py-20 px-4 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-balance">
            What Our <span className="text-orange-500">Travelers</span><span className="text-primary"> Say</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto text-pretty leading-relaxed">
            Don't just take our word for it - hear from the thousands of adventurers who have trusted us with their
            dream vacations
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-card border-border">
                <CardContent className="p-8">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded mb-4"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded"></div>
                      <div className="h-3 bg-gray-200 rounded"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>
                    <div className="mt-4">
                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Try Again
            </Button>
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">No testimonials available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {getVisibleTestimonials().map((testimonial) => (
                <TestimonialDisplayCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>

            {/* Pagination Dots */}
            {Math.ceil(testimonials.length / 3) > 1 && (
              <div className="flex justify-center space-x-2">
                {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                      index === currentIndex ? "bg-primary" : "bg-muted-foreground/30"
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <div className="text-center mt-12 animate-in fade-in duration-700" style={{ animationDelay: '400ms' }}>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 sm:p-8 lg:p-10 max-w-4xl mx-auto shadow-lg border border-amber-100">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-balance">Ready to Create Your Own <span className="autumn-text-gradient">Story?</span></h3>
            <p className="text-muted-foreground mb-6 text-base sm:text-lg text-pretty leading-relaxed">
              Join thousands of satisfied travelers and let us help you plan your next unforgettable adventure
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                variant="ghost"
                className="btn-autumn-secondary px-8 py-6 font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Link to="/destination">Plan My Trip</Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                className="btn-autumn-primary px-8 py-6 font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)', color: 'white' }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)'
                  e.currentTarget.style.color = 'white'
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)'
                  e.currentTarget.style.color = 'white'
                }}
              >
                <Link to="/contact">Get Free Consultation</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
